import { useState, useEffect, useRef } from 'react';
import { downloadXlsx, colName } from '../../utils/xlsxWriter';
import api from '../../hooks/useApi';
import { formatDate, formatMoney } from '../../utils/format';
import { canAccess } from '../../utils/rbac';
import Modal from '../shared/Modal';
import ColumnFilter, { useAnchoredCard } from '../shared/ColumnFilter';
import { resolveLayout, moveColumn, visibleColumns, passesColumnFilters } from '../../utils/columnLayout';
import { frameworkOptions, scopeToFramework } from '../../utils/frameworkView';

const RAG_COLORS = {
  red:   { bg: 'bg-red-100',     text: 'text-red-700',     dot: '#EF4444', label: 'Red'   },
  amber: { bg: 'bg-amber-100',   text: 'text-amber-700',   dot: '#F59E0B', label: 'Amber' },
  green: { bg: 'bg-emerald-100', text: 'text-emerald-700', dot: '#10B981', label: 'Green' },
};

function RagBadge({ rag }) {
  if (!rag) return <span className="text-gray-300 text-[9px]">—</span>;
  const c = RAG_COLORS[rag];
  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[8px] font-bold uppercase ${c.bg} ${c.text}`}>
      <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: c.dot }} />
      {c.label}
    </span>
  );
}

function dateDisplay(contract, forecast) {
  if (!contract && !forecast) return <span className="text-gray-300">—</span>;
  if (contract && forecast && contract !== forecast) {
    return (
      <span>
        <span className="text-gray-400 text-[8px]">C: </span>{formatDate(contract)}
        <span className="mx-1 text-gray-300">/</span>
        <span className="text-gray-400 text-[8px]">F: </span>{formatDate(forecast)}
      </span>
    );
  }
  return <span>{formatDate(forecast || contract)}</span>;
}

// Delivery-status colours follow the RAG rule: On Track = green, At Risk /
// On Hold = amber, Delayed / Blocked = red. Complete sits outside the rule
// with its own teal, so a finished project reads as finished rather than as
// one more healthy project still running.
const DELIVERY_STATUSES = [
  { value: 'on_track', label: 'On Track',  bg: 'bg-emerald-100', text: 'text-emerald-700' },
  { value: 'at_risk',  label: 'At Risk',   bg: 'bg-amber-100',   text: 'text-amber-700'   },
  { value: 'delayed',  label: 'Delayed',   bg: 'bg-red-100',     text: 'text-red-700'     },
  { value: 'blocked',  label: 'Blocked',   bg: 'bg-red-100',     text: 'text-red-700'     },
  { value: 'issued',   label: 'Issued',    bg: 'bg-blue-100',    text: 'text-blue-700'    },
  { value: 'on_hold',  label: 'On Hold',   bg: 'bg-amber-100',   text: 'text-amber-700'   },
  { value: 'uat',      label: 'UAT',       bg: 'bg-purple-100',  text: 'text-purple-700'  },
  { value: 'complete', label: 'Complete',  bg: 'bg-teal-100',    text: 'text-teal-700'    },
];

function DeliveryPill({ value }) {
  const ds = DELIVERY_STATUSES.find(d => d.value === value);
  if (!ds) return <span className="text-gray-300 text-[9px]">—</span>;
  return (
    <span className={`inline-flex px-1.5 py-0.5 rounded text-[8px] font-bold uppercase ${ds.bg} ${ds.text}`}>
      {ds.label}
    </span>
  );
}

// SIA = Service Impact Assessment. It is issued part way through delivery and
// used for service transition, so the reference is optional: most SOWs never
// carry one, and those that do only gain it mid-delivery. The rest of this
// dashboard is a read-only report, but this one cell is editable so the PMO can
// record the reference as soon as the SIA is issued, without leaving the page.
function SiaCell({ row, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue]     = useState(row.sia_reference || '');
  const [saving, setSaving]   = useState(false);
  const [failed, setFailed]   = useState(false);

  const startEdit = () => { setValue(row.sia_reference || ''); setFailed(false); setEditing(true); };

  const commit = async () => {
    setEditing(false);
    const next = value.trim();
    if (next === (row.sia_reference || '')) return;
    setSaving(true);
    setFailed(false);
    try {
      await api.put(`sows/${row.sow_id}`, { sia_reference: next || null });
      onSaved(next);
    } catch {
      setFailed(true);
    } finally {
      setSaving(false);
    }
  };

  if (editing) {
    return (
      <input
        autoFocus
        value={value}
        onChange={e => setValue(e.target.value)}
        onBlur={commit}
        onKeyDown={e => {
          if (e.key === 'Enter')  e.currentTarget.blur();
          if (e.key === 'Escape') { setValue(row.sia_reference || ''); setEditing(false); }
        }}
        placeholder="SIA ref…"
        className="w-[92px] text-[9px] font-mono border border-navy rounded px-1 py-0.5 focus:outline-none"
      />
    );
  }

  return (
    <div>
      <button
        type="button"
        onClick={startEdit}
        title={row.sia_reference
          ? `Service Impact Assessment ${row.sia_reference} — click to edit`
          : 'No Service Impact Assessment issued yet — click to add the reference'}
        className={`text-left rounded px-1 py-0.5 hover:bg-blue-50 hover:ring-1 hover:ring-blue-200 ${row.sia_reference ? 'font-mono text-[9px] text-gray-600' : 'text-[8px] text-gray-300'}`}
      >
        {saving ? 'Saving…' : (row.sia_reference || '+ add')}
      </button>
      {failed && <div className="text-[7px] text-red-600 font-semibold">Save failed</div>}
    </div>
  );
}

// The SOW's client contact opens a card in place rather than linking away: the
// pmo role has no CRM Contacts grant, so "Open in CRM" only shows for those who
// can follow it.
function ClientContactCell({ row }) {
  const { anchor, setAnchor, wrapRef, open } = useAnchoredCard();

  if (!row.client_contact) return <span className="text-gray-300">—</span>;

  // Open below the name, or above it when too near the bottom of the window.
  const place = anchor && (anchor.bottom + 160 > anchor.vh
    ? { bottom: anchor.vh - anchor.top + 4 }
    : { top: anchor.bottom + 4 });

  return (
    <span ref={wrapRef}>
      <button
        type="button"
        aria-expanded={!!anchor}
        onClick={e => (anchor ? setAnchor(null) : open(e.currentTarget))}
        className="block max-w-[130px] truncate text-left text-blue-600 hover:underline"
        title={row.client_contact}
      >
        {row.client_contact}
      </button>
      {anchor && (
        <div
          role="dialog"
          aria-label={`Contact details for ${row.client_contact}`}
          className="fixed z-50 w-56 bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-[10px] space-y-1"
          style={{ left: Math.min(anchor.left, anchor.vw - 232), ...place }}
        >
          <div className="font-bold text-navy">{row.client_contact}</div>
          {(row.client_contact_role || row.client_name) && (
            <div className="text-gray-500">{[row.client_contact_role, row.client_name].filter(Boolean).join(' · ')}</div>
          )}
          {row.client_contact_email && (
            <a href={`mailto:${row.client_contact_email}`} className="block truncate text-blue-600 hover:underline">{row.client_contact_email}</a>
          )}
          {row.client_contact_phone && (
            <a href={`tel:${row.client_contact_phone}`} className="block text-gray-700 hover:underline">{row.client_contact_phone}</a>
          )}
          {canAccess('crm-contacts') && (
            <a href={`#crm-contacts/${row.client_contact_id}`} className="block pt-1 font-semibold text-blue-600 hover:underline">Open in CRM →</a>
          )}
        </div>
      )}
    </span>
  );
}

const RAG_LABELS = { schedule_rag: 'Schedule', budget_rag: 'Budget', resource_rag: 'Resource', risk_rag: 'Risk' };

function RagPicker({ label, value, onChange }) {
  return (
    <div>
      <div className="text-[7px] font-bold uppercase text-gray-500 mb-1">{label}</div>
      <div className="flex gap-1">
        {['red','amber','green'].map(v => {
          const c = RAG_COLORS[v];
          return (
            <button key={v} type="button" onClick={() => onChange(value === v ? '' : v)}
              className={`w-7 h-6 rounded text-[8px] font-bold uppercase transition-all ${c.bg} ${c.text} ${value === v ? 'ring-2 ring-offset-1 ring-current scale-105' : 'opacity-30 hover:opacity-70'}`}
              title={c.label}
            >{v[0].toUpperCase()}</button>
          );
        })}
        <button type="button" onClick={() => onChange('')}
          className={`w-7 h-6 rounded text-[8px] font-bold uppercase transition-all bg-gray-100 text-gray-400 ${!value ? 'ring-2 ring-offset-1 ring-gray-400 scale-105' : 'opacity-40 hover:opacity-70'}`}
          title="Clear"
        >—</button>
      </div>
    </div>
  );
}

// Dormant — the PMO dashboard is a read-only report (edit pencil removed). Kept
// for a future, RR-permissioned "RR Actions / Comment" editor; not wired in.
// eslint-disable-next-line no-unused-vars
function PmoEditModal({ row, onClose, onSaved }) {
  const origRag = {
    schedule_rag: row.schedule_rag || '',
    budget_rag:   row.budget_rag   || '',
    resource_rag: row.resource_rag || '',
    risk_rag:     row.risk_rag     || '',
  };
  const [form, setForm] = useState({
    delivery_status:  row.delivery_status  || '',
    recent_progress:  row.recent_progress  || '',
    next_period:      row.next_period      || '',
    risks_issues:     row.risks_issues     || '',
    schedule_rag:     row.schedule_rag     || '',
    budget_rag:       row.budget_rag       || '',
    resource_rag:     row.resource_rag     || '',
    risk_rag:         row.risk_rag         || '',
    pmo_override_note: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState(null);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const ragChanged = Object.keys(origRag).some(k => form[k] !== origRag[k]);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      let result;
      if (row.rag_id) {
        result = await api.put(`pmo-dashboard/${row.rag_id}`, form);
      } else {
        result = await api.post('pmo-dashboard', { sow_id: row.sow_id, ...form });
      }
      onSaved({
        ...form,
        rag_id: result.rag_id || row.rag_id,
        rag_updated_by_name: result.updated_by_name,
        pmo_override_note: result.pmo_override_note,
      });
      onClose();
    } catch (e) {
      setError(e?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal wide title="Edit PMO Dashboard Entry" onClose={onClose}>
        <div className="text-[9px] text-gray-500 mb-4">{row.reference} · {row.client_name || 'No client'}</div>

        {/* RAG overrides */}
        <div className="mb-4 p-3 bg-gray-50 border border-gray-200 rounded-lg">
          <div className="text-[9px] font-bold text-gray-600 uppercase mb-2 flex items-center gap-1.5">
            RAG Status Override
            {ragChanged && <span className="px-1.5 py-0.5 bg-amber-100 text-amber-700 text-[7px] rounded font-bold">Changed — will be tracked</span>}
          </div>
          <div className="grid grid-cols-4 gap-3">
            {Object.entries(RAG_LABELS).map(([key, label]) => (
              <RagPicker key={key} label={label} value={form[key]} onChange={v => set(key, v)} />
            ))}
          </div>
          {ragChanged && (
            <div className="mt-3">
              <label className="block text-[8px] font-semibold text-amber-700 uppercase mb-1">Reason for override <span className="font-normal text-gray-400">(optional — visible to PM)</span></label>
              <input
                type="text"
                className="form-input text-[9px] w-full border-amber-300"
                placeholder="e.g. Client requested RAG escalation in review meeting"
                value={form.pmo_override_note}
                onChange={e => set('pmo_override_note', e.target.value)}
              />
            </div>
          )}
        </div>

        <div className="space-y-3">
          <div>
            <label className="block text-[9px] font-semibold text-gray-600 uppercase mb-1">Delivery Status</label>
            <select value={form.delivery_status} onChange={e => set('delivery_status', e.target.value)} className="form-input text-[10px] w-full">
              <option value="">— Not set —</option>
              {DELIVERY_STATUSES.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
            </select>
          </div>

          <div>
            <label className="block text-[9px] font-semibold text-gray-600 uppercase mb-1">Recent Progress</label>
            <textarea value={form.recent_progress} onChange={e => set('recent_progress', e.target.value)} rows={3} className="form-input text-[10px] w-full resize-y" placeholder="What was completed this period…" />
          </div>

          <div>
            <label className="block text-[9px] font-semibold text-gray-600 uppercase mb-1">Next Period Notes</label>
            <textarea value={form.next_period} onChange={e => set('next_period', e.target.value)} rows={3} className="form-input text-[10px] w-full resize-y" placeholder="What's planned for next period…" />
          </div>

          <div>
            <label className="block text-[9px] font-semibold text-gray-600 uppercase mb-1">Risks &amp; Issues</label>
            <textarea value={form.risks_issues} onChange={e => set('risks_issues', e.target.value)} rows={3} className="form-input text-[10px] w-full resize-y" placeholder="Active risks or issues…" />
          </div>
        </div>

        {error && <div className="mt-3 text-[9px] text-red-600 bg-red-50 rounded px-2 py-1">{error}</div>}

        <div className="flex justify-end gap-2 mt-4 pt-3 border-t border-gray-100 sticky bottom-0 -mx-4 -mb-3 px-4 pb-3 bg-white">
          <button onClick={onClose} className="px-3 py-1.5 text-[10px] text-gray-600 hover:text-gray-800 border border-gray-200 rounded">Cancel</button>
          <button onClick={handleSave} disabled={saving} className="px-4 py-1.5 text-[10px] font-semibold bg-navy text-white rounded hover:bg-navy/90 disabled:opacity-50">
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
    </Modal>
  );
}

// RAG value → Excel fill colour (ARGB hex, no leading #)
const RAG_FILL = { red: 'FFEF4444', amber: 'FFF59E0B', green: 'FF10B981' };
const RAG_TEXT = { red: 'FFFFFFFF', amber: 'FF1C1917', green: 'FFFFFFFF' };
const HDR_FILL = '0D1D32'; // navy
const SUBHDR_FILL = '1E3A5F'; // lighter navy for section groups

const cellStyle = (opts = {}) => ({
  font: { bold: opts.bold, color: { rgb: opts.fontColor || 'FF000000' }, sz: opts.sz || 10 },
  fill: opts.fill ? { fgColor: { rgb: opts.fill } } : undefined,
  alignment: { horizontal: opts.align || 'left', vertical: 'center', wrapText: opts.wrap },
  border: {
    top:    { style: 'thin', color: { rgb: 'FFD1D5DB' } },
    bottom: { style: 'thin', color: { rgb: 'FFD1D5DB' } },
    left:   { style: 'thin', color: { rgb: 'FFD1D5DB' } },
    right:  { style: 'thin', color: { rgb: 'FFD1D5DB' } },
  },
});

const fmt = (v) => (v == null || v === '') ? '' : String(v);
const fmtMoney = (v) => (v == null || v === '') ? '' : Number(v).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtRag = (v) => v ? v.charAt(0).toUpperCase() + v.slice(1) : '';
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
// Client-facing report dates: dd-mon-yyyy (e.g. 24-Jun-2026).
const fmtXlDate = (v) => {
  if (v == null || v === '') return '';
  const m = String(v).slice(0, 10).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return String(v);
  return `${m[3]}-${MONTHS[+m[2] - 1]}-${m[1]}`;
};
// Real JS Date for Excel so date columns sort chronologically rather than as
// text. UTC noon avoids a timezone shifting the day. Null for blanks/unparseable.
const toXlDate = (v) => {
  if (v == null || v === '') return null;
  const m = String(v).slice(0, 10).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return null;
  return new Date(Date.UTC(+m[1], +m[2] - 1, +m[3], 12));
};
const XL_DATE_FMT = 'dd-mmm-yyyy';

// The Dependency and Risks & Issues columns come from the project's RAID
// register (open items for the SOW), delivered as JSON arrays. These parse and
// serialise them, so both the screen and the export show every logged item.
const parseRaidArr = (s) => {
  if (!s) return [];
  if (Array.isArray(s)) return s;
  try { const a = JSON.parse(s); return Array.isArray(a) ? a : []; } catch { return []; }
};
const raidDepsText = (s) => parseRaidArr(s).map(d => {
  const head = [d.party, d.title].filter(Boolean).join(': ');
  return [head || d.ref, d.detail].filter(Boolean).join(' — ');
}).join('\n');
const raidRisksText = (s) => parseRaidArr(s).map(x =>
  `${x.type === 'issue' ? 'Issue' : 'Risk'}: ${x.title}${x.reason ? ` (${x.reason})` : ''}`
).join('\n');

// Export is client-facing and holds exactly what the table is showing: its
// rows, so a client's report is made by filtering the Client column first, and
// the columns not hidden on screen. There is no Client column in it. The client
// contact is simply "Client", and the ATV-side PM is labelled so. Internal-only
// fields (client email, PMO override note) are omitted.
//
// Each export column names the dashboard column it belongs to (`col`) and is
// dropped when that one is hidden; Client PO has no column of its own and
// travels with SOW, which cannot be hidden. Priority is not on the dashboard,
// so it is not exported either. `kind` styles it:
// rag and delivery cells are coloured, money is right-aligned in green, dates
// are real dates, wrap is narrative.
const msText = (code, desc) => code ? `${code}${desc ? ' — ' + desc : ''}` : '';
const EXPORT_COLUMNS = [
  // SOW identity
  { header: 'SOW Ref',               col: 'sow',             wch: 14, value: r => fmt(r.reference) },
  { header: 'Detail',                col: 'sow',             wch: 34, value: r => fmt(r.detail) },
  { header: 'Client PO',             col: 'sow',             wch: 18, value: r => fmt(r.po_number) },
  { header: 'ATV PM',                col: 'owner',           wch: 22, value: r => fmt(r.owner_name) },
  // Milestone pipeline
  { header: 'Next Milestone',        col: 'next_ms',         wch: 34, value: r => msText(r.next_ms_code, r.next_ms_desc) },
  { header: 'Next MS Contract Date', col: 'next_ms_date',    wch: 13, kind: 'date', value: r => toXlDate(r.next_contract_date) },
  { header: 'Next MS Forecast Date', col: 'next_ms_date',    wch: 13, kind: 'date', value: r => toXlDate(r.next_forecast_date) },
  { header: 'Ms Status',             col: 'next_ms',         wch: 14, value: r => fmt(r.next_ms_status) },
  { header: 'Missed Reason',         col: 'next_ms',         wch: 30, value: r => fmt(r.next_ms_missed_reason) },
  // RAG (coloured)
  { header: 'Overall RAG',           col: 'rag',             wch: 10, kind: 'rag', value: r => fmtRag(r.worst_rag) },
  { header: 'Schedule',              col: 'rag',             wch: 10, kind: 'rag', value: r => fmtRag(r.schedule_rag) },
  { header: 'Budget',                col: 'rag',             wch: 10, kind: 'rag', value: r => fmtRag(r.budget_rag) },
  { header: 'Resource',              col: 'rag',             wch: 10, kind: 'rag', value: r => fmtRag(r.resource_rag) },
  { header: 'Risk',                  col: 'rag',             wch: 10, kind: 'rag', value: r => fmtRag(r.risk_rag) },
  // Delivery status (Blocked → red)
  { header: 'Delivery Status',       col: 'status',          wch: 14, kind: 'delivery', value: r => fmt(r.delivery_status ? r.delivery_status.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : '') },
  // RAID narrative
  { header: 'Recent Progress',       col: 'recent_progress', wch: 44, kind: 'wrap', value: r => fmt(r.recent_progress) },
  { header: 'Next Period Plans',     col: 'next_period',     wch: 44, kind: 'wrap', value: r => fmt(r.next_period) },
  { header: 'Risks & Issues',        col: 'risks',           wch: 44, kind: 'wrap', value: r => raidRisksText(r.raid_risks_issues) || fmt(r.risks_issues) },
  // Final milestone
  { header: 'Final Milestone',       col: 'final_ms',        wch: 34, value: r => msText(r.final_ms_code, r.final_ms_desc) },
  { header: 'Final Contract',        col: 'final_date',      wch: 13, kind: 'date', value: r => toXlDate(r.final_contract_date) },
  { header: 'Final Forecast',        col: 'final_date',      wch: 13, kind: 'date', value: r => toXlDate(r.final_forecast_date) },
  // Financial
  { header: 'SOW Value (£)',         col: 'value',           wch: 14, kind: 'money', value: r => fmtMoney(r.total_excl_vat) },
  { header: 'Invoiced (£)',          col: 'value',           wch: 14, kind: 'money', value: r => fmtMoney(r.invoiced_sum) },
  { header: 'Remaining (£)',         col: 'value',           wch: 14, kind: 'money', value: r => fmtMoney(r.remaining_to_invoice) },
  // Metadata: the RAG cell shows its age, Risks & Issues who updated it
  { header: 'RAG Last Updated',      col: 'rag',             wch: 14, kind: 'date', value: r => toXlDate(r.rag_submitted_at) },
  { header: 'Updated By',            col: 'risks',           wch: 22, value: r => fmt(r.rag_updated_by_name) },
  // Dependency (milestone) + RR (project) narrative
  { header: 'Dependency',            col: 'dependency',      wch: 40, kind: 'wrap', value: r => raidDepsText(r.raid_dependencies) || [r.dependency_reason, r.dependency_comment].filter(Boolean).join(' — ') },
  { header: 'RR Actions / Comment',  col: 'rr',              wch: 44, kind: 'wrap', value: r => [r.rr_actions ? `Actions: ${r.rr_actions}` : '', r.rr_comment ? `Comment: ${r.rr_comment}` : ''].filter(Boolean).join('\n') },
  // RR reference columns
  { header: 'Type',                  col: 'type',            wch: 16, value: r => fmt(r.sow_type) },
  { header: 'Client Contact',        col: 'contact',         wch: 22, value: r => fmt(r.client_contact) },
  { header: 'DS Key',                col: 'ds_key',          wch: 16, value: r => fmt(r.ds_key) },
  // Service Impact Assessment reference (optional, issued mid-delivery)
  { header: 'SIA Ref',               col: 'sia',             wch: 16, value: r => fmt(r.sia_reference) },
];

// `shown` is the dashboard's visible columns, as the table draws them.
function exportToExcel(rows, shown) {
  const clients = [...new Set(rows.map(r => r.client_name).filter(Boolean))].sort();
  const keys = new Set(shown.map(c => c.key));
  const columns = EXPORT_COLUMNS.filter(c => keys.has(c.col));

  const generatedAt = new Date().toLocaleString('en-GB', { dateStyle: 'full', timeStyle: 'short' });

  // ── Build workbook ──────────────────────────────────────────────────────────
  // The data goes on its own sheet with the header on row 1 (no banner rows
  // above it) so Excel sorts/filters it cleanly — the previous merged title
  // banner sat above the table and blocked sorting. Context now lives on a
  // separate "Overview" sheet. Written by utils/xlsxWriter.js (pen test
  // AD25996, AD17), which also writes the frozen panes this always asked for.

  // Header row (row index 0)
  const headerCells = columns.map(({ header, kind }) => {
    const isRagHdr = kind === 'rag';
    const isMoneyHdr = kind === 'money';
    return {
      v: header,
      s: cellStyle({
        bold: true,
        fontColor: 'FFFFFFFF',
        fill: isRagHdr ? '7C3AED' : isMoneyHdr ? '065F46' : SUBHDR_FILL,
        align: (isRagHdr || isMoneyHdr) ? 'center' : 'left',
      }),
    };
  });

  // Data rows (row index 1+)
  const dataCells = rows.map((r, i) => {
    const ri = i + 1;
    return columns.map(({ kind, value }) => {
      const v = value(r) ?? '';
      const isRag   = kind === 'rag';
      const isMoney = kind === 'money';
      const isDate  = kind === 'date';
      const isAlt   = (ri % 2 === 0);
      // Date/number cells hold non-string values — only lowercase real strings.
      const val     = (typeof v === 'string' ? v : '').toLowerCase();
      let cellFill = isRag && RAG_FILL[val] ? RAG_FILL[val] : null;
      let cellText = isRag && RAG_TEXT[val] ? RAG_TEXT[val] : null;
      // Colour the delivery-status cell by the RAG rule: On Track = green,
      // At Risk / On Hold = amber, Delayed / Blocked = red.
      if (kind === 'delivery') {
        const dsRag = { 'on track': 'green', 'at risk': 'amber', 'on hold': 'amber', 'delayed': 'red', 'blocked': 'red', 'complete': 'green' }[val];
        if (dsRag) { cellFill = RAG_FILL[dsRag]; cellText = RAG_TEXT[dsRag]; }
      }
      return {
        v,
        // Real date cells get a date number format so Excel sorts them as dates.
        z: isDate && v instanceof Date ? XL_DATE_FMT : undefined,
        s: cellStyle({
          bold:      !!cellFill,
          fill:      cellFill || (isMoney ? (isAlt ? 'FFF0FDF4' : 'FFECFDF5') : (isAlt ? 'FFF8FAFC' : 'FFFFFFFF')),
          fontColor: cellText || (isMoney ? 'FF065F46' : 'FF1F2937'),
          align:     isRag ? 'center' : isMoney ? 'right' : 'left',
          wrap:      kind === 'wrap',
        }),
      };
    });
  });

  const dashboard = {
    name: 'PMO Dashboard',
    rows: [headerCells, ...dataCells],
    cols: columns.map(c => ({ wch: c.wch })),
    // Header row height; data rows taller for wrapped text
    heights: [{ hpt: 22 }, ...rows.map(() => ({ hpt: 40 }))],
    // Freeze the header row + first two columns (SOW Ref and Detail)
    freeze: { xSplit: 2, ySplit: 1 },
    // Auto-filter from the header row over the data
    autofilter: `A1:${colName(columns.length - 1)}${rows.length + 1}`,
  };

  // ── Overview sheet — banner + summary, kept off the data table ──
  const cnt = (pred) => rows.filter(pred).length;
  const ovRows = [
    ['Alton Valley — PMO Dashboard'],
    [clients.length === 1 ? `Client: ${clients[0]}` : `Clients: ${clients.join(', ') || 'none'}`],
    [`Generated: ${generatedAt}`],
    [`Active SOWs: ${rows.length}`],
    [],
    ['Overall RAG', 'Count'],
    ['Red',   cnt(r => r.worst_rag === 'red')],
    ['Amber', cnt(r => r.worst_rag === 'amber')],
    ['Green', cnt(r => r.worst_rag === 'green')],
    [],
    ['Delivery Status', 'Count'],
    ['Blocked',  cnt(r => r.delivery_status === 'blocked')],
    ['Delayed',  cnt(r => r.delivery_status === 'delayed')],
    ['At Risk',  cnt(r => r.delivery_status === 'at_risk')],
    ['On Track', cnt(r => r.delivery_status === 'on_track')],
  ];
  const ovTitleStyle = cellStyle({ bold: true, fontColor: 'FFFFFFFF', fill: HDR_FILL, sz: 12 });
  const ovHeadStyle = cellStyle({ bold: true, fontColor: 'FFFFFFFF', fill: SUBHDR_FILL });
  const overview = {
    name: 'Overview',
    rows: ovRows.map((row, rr) => row.map(v => ({
      v,
      s: rr === 0 ? ovTitleStyle : (rr === 5 || rr === 10) ? ovHeadStyle : undefined,
    }))),
    cols: [{ wch: 28 }, { wch: 10 }],
    merges: [{ s: { r: 0, c: 0 }, e: { r: 0, c: 1 } }],
  };

  const slug = (clients.length === 1 ? clients[0] : 'selection').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  downloadXlsx([dashboard, overview], `pmo-dashboard-${slug}-${new Date().toISOString().slice(0, 10)}.xlsx`);
}

// Health flags (computed from the next pending milestone + RAG freshness).
const todayIso  = () => new Date().toISOString().split('T')[0];
const isOverdue = (r) => r.next_forecast_date && r.next_forecast_date < todayIso();
const isSlipped = (r) => r.next_forecast_date && r.next_contract_date && r.next_forecast_date > r.next_contract_date;
const slipDays  = (r) => isSlipped(r) ? Math.round((Date.parse(r.next_forecast_date) - Date.parse(r.next_contract_date)) / 86400000) : 0;

const has = (s, q) => (s || '').toLowerCase().includes(q.toLowerCase());
const dash = <span className="text-gray-300">—</span>;
const dashSm = <span className="text-gray-300 text-[9px]">—</span>;

// The table's columns, in their default order. Each person can hide them and
// drag them into their own order (see utils/columnLayout); the SOW column is
// locked visible, as it is the row's link to its record. A column's filter
// sits in the row under its header, and a hidden column does not filter. A
// filter is a text box (`text`, matched by `match`) or a pick-list of the
// row's `value`, any number of which can be ticked; its options are the
// values the rows hold unless `options` lists them.
const COLUMNS = [
  {
    key: 'sow', label: 'SOW', locked: true, td: 'whitespace-nowrap',
    filter: { text: 'Filter SOW…' },
    match: (r, q) => has(r.reference, q) || has(r.detail, q),
    cell: (r, ctx) => (
      <>
        {ctx.framework && r.sow_id !== ctx.framework && <span className="mr-1 text-gray-300" aria-hidden="true">↳</span>}
        <a href={`#sow-detail/${r.sow_id}`} className="font-bold text-navy hover:underline">{r.reference}</a>
        {r.detail && <div className="text-[8px] text-gray-400 truncate max-w-[140px]" title={r.detail}>{r.detail}</div>}
        {r.framework_pots?.map(p => (
          <div key={p.label} className="text-[8px] text-gray-500">
            <span className="text-gray-400">{p.label} </span>{formatMoney(p.called_off)}{p.value != null && ` of ${formatMoney(p.value)}`} called off
          </div>
        ))}
        {r.hardware_unresolved_count > 0 && (
          <div className="mt-0.5 inline-flex items-center gap-0.5 px-1 py-0.5 bg-red-100 border border-red-200 rounded text-[7px] font-bold text-red-700" title={`${r.hardware_unresolved_count} hardware item${r.hardware_unresolved_count !== 1 ? 's' : ''} not ordered and no stock available`}>
            ⚠ {r.hardware_unresolved_count} hw needs ordering
          </div>
        )}
      </>
    ),
  },
  {
    key: 'client', label: 'Client', td: 'whitespace-nowrap text-gray-700',
    filter: { all: 'All clients', value: r => r.client_name },
    cell: r => r.client_name || dash,
  },
  {
    key: 'owner', label: 'Owner', td: 'whitespace-nowrap text-gray-600',
    filter: { all: 'All owners', value: r => r.owner_name },
    cell: r => r.owner_name || dash,
  },
  {
    key: 'type', label: 'Type', td: 'whitespace-nowrap text-gray-600',
    filter: { all: 'All types', value: r => r.sow_type },
    cell: r => r.sow_type || dash,
  },
  {
    // Contract value, ex VAT: billed is Invoiced + Paid milestones
    key: 'value', label: 'Contract Value', title: 'Excluding VAT. Billed = milestones Invoiced or Paid', td: 'whitespace-nowrap text-gray-700',
    cell: r => (
      <>
        <div className="font-semibold text-gray-800">{formatMoney(r.total_excl_vat)}</div>
        <div className="text-[8px] text-gray-500"><span className="text-gray-400">Billed </span>{formatMoney(r.invoiced_sum)}</div>
        <div className="text-[8px] text-gray-500"><span className="text-gray-400">To bill </span>{formatMoney(r.remaining_to_invoice)}</div>
      </>
    ),
  },
  {
    key: 'contact', label: 'Client Contact', td: 'text-gray-600',
    filter: { all: 'All contacts', value: r => r.client_contact },
    cell: r => <ClientContactCell row={r} />,
  },
  {
    // DS Key (SOW client reference)
    key: 'ds_key', label: 'DS Key', td: 'whitespace-nowrap font-mono text-[9px] text-gray-600',
    filter: { text: 'Filter DS Key…' },
    match: (r, q) => has(r.ds_key, q),
    cell: r => r.ds_key || <span className="text-gray-300 font-sans">—</span>,
  },
  {
    // SIA Ref — Service Impact Assessment, added mid-delivery
    key: 'sia', label: 'SIA Ref', title: 'Service Impact Assessment reference — optional, issued part way through delivery for service transition', td: 'whitespace-nowrap',
    filter: { text: 'Filter SIA…' },
    match: (r, q) => has(r.sia_reference, q),
    cell: (r, ctx) => <SiaCell row={r} onSaved={v => ctx.setSiaReference(r.sow_id, v)} />,
  },
  {
    key: 'next_ms', label: 'Next Milestone',
    cell: r => r.next_ms_id ? (
      <>
        <a href={`#sow-detail/${r.sow_id}/milestone-${r.next_ms_id}`} className="font-semibold text-gray-800 hover:underline">{r.next_ms_code}</a>
        {r.next_ms_desc && (
          <div className="text-[8px] text-gray-400 max-w-[180px] truncate" title={r.next_ms_desc}>{r.next_ms_desc}</div>
        )}
        {r.next_ms_status && (
          <div className="mt-0.5">
            <span className="text-[7px] px-1 py-0.5 bg-gray-100 text-gray-500 rounded uppercase font-semibold">{r.next_ms_status}</span>
          </div>
        )}
        {r.next_ms_missed_reason && (
          <div className="mt-1 flex items-start gap-1">
            <span className="text-[7px] px-1.5 py-0.5 bg-red-100 text-red-700 rounded font-semibold uppercase whitespace-nowrap">Missed</span>
            <span className="text-[7px] text-red-600 leading-tight">{r.next_ms_missed_reason}</span>
          </div>
        )}
        {r.next_ms_dependency_reason && (
          <div className="mt-1 flex items-start gap-1" title={r.next_ms_dependency_comment || ''}>
            <span className="text-[7px] px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded font-semibold uppercase whitespace-nowrap">Dep</span>
            <span className="text-[7px] text-amber-700 leading-tight max-w-[150px] line-clamp-2">
              {r.next_ms_dependency_reason}{r.next_ms_dependency_comment ? ` — ${r.next_ms_dependency_comment}` : ''}
            </span>
          </div>
        )}
      </>
    ) : (
      <span className="text-gray-300 text-[9px]">No pending milestones</span>
    ),
  },
  {
    // Contract / Forecast date for next milestone
    key: 'next_ms_date', label: 'Next MS Contract / Forecast', td: 'whitespace-nowrap text-gray-700',
    cell: r => r.next_ms_id ? (
      <div className="flex flex-col gap-0.5">
        {dateDisplay(r.next_contract_date, r.next_forecast_date)}
        {isOverdue(r) && <span className="text-[7px] font-bold text-red-600 uppercase">Overdue</span>}
        {!isOverdue(r) && isSlipped(r) && <span className="text-[7px] font-bold text-orange-600">at risk +{slipDays(r)}d</span>}
      </div>
    ) : dash,
  },
  {
    // Worst RAG
    key: 'rag', label: 'RAG', align: 'center', td: 'text-center',
    cell: r => RAG_COLORS[r.worst_rag] ? (
      <div className="space-y-0.5">
        <RagBadge rag={r.worst_rag} />
        <div className="flex justify-center gap-0.5 mt-1">
          {[r.schedule_rag, r.budget_rag, r.resource_rag, r.risk_rag].map((v, i) => (
            <span
              key={i}
              className="w-2 h-2 rounded-full flex-shrink-0"
              style={{ background: v ? RAG_COLORS[v]?.dot : '#E5E7EB' }}
              title={['Schedule','Budget','Resource','Risk'][i] + ': ' + (v || 'N/A')}
            />
          ))}
        </div>
        {r.pmo_override_note && (
          <div className="mt-0.5" title={`PMO override: ${r.pmo_override_note}`}>
            <span className="text-[6px] font-bold px-1 py-px bg-amber-100 text-amber-700 rounded uppercase">PMO override</span>
          </div>
        )}
        <div
          className={`text-[7px] mt-0.5 ${r.rag_stale ? 'text-red-600 font-bold' : 'text-gray-400'}`}
          title={r.rag_week_commencing ? `Last health update: ${r.rag_week_commencing}` : 'No health update recorded'}
        >
          {r.rag_age_days == null ? 'never updated'
            : r.rag_age_days === 0 ? 'updated today'
            : `updated ${r.rag_age_days}d ago`}{r.rag_stale ? ' ⚠' : ''}
        </div>
      </div>
    ) : (
      <span className="text-[8px] font-bold text-red-600">No update ⚠</span>
    ),
  },
  {
    // Delivery Status
    key: 'status', label: 'Status',
    filter: { all: 'All statuses', value: r => r.delivery_status, options: () => DELIVERY_STATUSES.map(d => [d.value, d.label]) },
    cell: r => <DeliveryPill value={r.delivery_status} />,
  },
  {
    key: 'recent_progress', label: 'Recent Progress',
    cell: r => r.recent_progress ? (
      <div className="text-gray-700 text-[9px] max-w-[200px] whitespace-pre-wrap line-clamp-3" title={r.recent_progress}>
        {r.recent_progress}
      </div>
    ) : dashSm,
  },
  {
    key: 'next_period', label: 'Next Period Notes',
    cell: r => (
      <>
        {r.next_period ? (
          <div className="text-gray-700 text-[9px] max-w-[200px] whitespace-pre-wrap line-clamp-3" title={r.next_period}>
            {r.next_period}
          </div>
        ) : dashSm}
        {r.rag_week_commencing && (
          <div className="text-[7px] text-gray-300 mt-0.5">w/c {formatDate(r.rag_week_commencing)}</div>
        )}
      </>
    ),
  },
  {
    // Risks & Issues — from the project's RAID register (open items)
    key: 'risks', label: 'Risks & Issues',
    cell: r => {
      const items = parseRaidArr(r.raid_risks_issues);
      return (
        <>
          {items.length ? (
            <div className="max-w-[200px] space-y-0.5">
              {items.map((x, i) => (
                <div key={i} className="flex items-start gap-1" title={[x.ref, x.reason].filter(Boolean).join(' · ')}>
                  <span className={`text-[7px] px-1 py-0.5 rounded font-bold uppercase flex-shrink-0 ${x.type === 'issue' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>{x.type === 'issue' ? 'I' : 'R'}</span>
                  <span className="text-[9px] text-gray-700 leading-tight line-clamp-2">{x.title}{x.reason ? <span className="text-gray-400"> · {x.reason}</span> : null}</span>
                </div>
              ))}
            </div>
          ) : r.risks_issues
            ? <div className="text-gray-700 text-[9px] max-w-[200px] whitespace-pre-wrap line-clamp-3" title={r.risks_issues}>{r.risks_issues}</div>
            : dashSm}
          {r.rag_updated_by_name && (
            <div className="text-[7px] text-indigo-400 mt-0.5 flex items-center gap-0.5">
              <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
              {r.rag_updated_by_name}
            </div>
          )}
          {r.pmo_override_note && (
            <div className="text-[7px] text-amber-600 mt-0.5 italic leading-tight">
              PMO: {r.pmo_override_note}
            </div>
          )}
        </>
      );
    },
  },
  {
    // Dependency — from the project's RAID register (open items); falls back
    // to a live milestone-level dependency. Never shows legacy Zoho cutover data.
    key: 'dependency', label: 'Dependency',
    cell: r => {
      const deps = parseRaidArr(r.raid_dependencies);
      if (deps.length) {
        return (
          <div className="max-w-[180px] space-y-1">
            {deps.map((d, i) => (
              <div key={i} title={[d.ref, d.detail].filter(Boolean).join(' · ')}>
                <div className="inline-flex items-center gap-1 flex-wrap">
                  {d.party && <span className="text-[7px] px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded font-semibold uppercase whitespace-nowrap">{d.party}</span>}
                  <span className="text-[9px] text-gray-700 leading-tight">{d.title}</span>
                </div>
                {d.detail && <div className="text-gray-500 text-[8px] whitespace-pre-wrap line-clamp-2">{d.detail}</div>}
              </div>
            ))}
          </div>
        );
      }
      return r.dependency_reason ? (
        <div className="max-w-[180px]">
          <div className="inline-flex items-center gap-1">
            <span className="text-[7px] px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded font-semibold uppercase whitespace-nowrap">{r.dependency_reason}</span>
            {r.dep_ms_code && <span className="text-[7px] text-gray-400">{r.dep_ms_code}</span>}
          </div>
          {r.dependency_comment && (
            <div className="text-gray-600 text-[9px] mt-0.5 whitespace-pre-wrap line-clamp-3" title={r.dependency_comment}>{r.dependency_comment}</div>
          )}
        </div>
      ) : dashSm;
    },
  },
  {
    // RR Actions / Comment (project/SOW level)
    key: 'rr', label: 'RR Actions / Comment',
    cell: r => (r.rr_actions || r.rr_comment) ? (
      <div className="max-w-[200px] space-y-0.5">
        {r.rr_actions && (
          <div className="text-gray-700 text-[9px] whitespace-pre-wrap line-clamp-3" title={r.rr_actions}>
            <span className="font-semibold text-gray-400">Actions: </span>{r.rr_actions}
          </div>
        )}
        {r.rr_comment && (
          <div className="text-gray-600 text-[9px] whitespace-pre-wrap line-clamp-2" title={r.rr_comment}>
            <span className="font-semibold text-gray-400">Comment: </span>{r.rr_comment}
          </div>
        )}
      </div>
    ) : dashSm,
  },
  {
    key: 'final_ms', label: 'Final Milestone',
    cell: r => r.final_ms_code ? (
      <>
        <a href={`#sow-detail/${r.sow_id}/milestone-${r.final_ms_id}`} className="font-semibold text-gray-800 hover:underline">{r.final_ms_code}</a>
        {r.final_ms_desc && (
          <div className="text-[8px] text-gray-400 max-w-[160px] truncate" title={r.final_ms_desc}>{r.final_ms_desc}</div>
        )}
      </>
    ) : dashSm,
  },
  {
    key: 'final_date', label: 'Final Date', td: 'whitespace-nowrap text-gray-700',
    cell: r => r.final_ms_code ? dateDisplay(r.final_contract_date, r.final_forecast_date) : dash,
  },
];

// The layout is the person's own and lives in their browser. Storage can be
// blocked (private windows), in which case they get the default every visit.
const LAYOUT_KEY = 'pmo_dashboard_columns';
function loadLayout() {
  try { return resolveLayout(COLUMNS, JSON.parse(localStorage.getItem(LAYOUT_KEY))); }
  catch { return resolveLayout(COLUMNS, null); }
}

// Shared by the header cells and the Columns menu, so a column can be dragged
// in either place. A drop of anything that is not a column key is ignored.
const dragProps = (key, move) => ({
  draggable: true,
  onDragStart: e => { e.dataTransfer.setData('text/plain', key); e.dataTransfer.effectAllowed = 'move'; },
  onDragOver:  e => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; },
  onDrop:      e => { e.preventDefault(); move(e.dataTransfer.getData('text/plain'), key); },
});

function ColumnsMenu({ layout, onChange, move }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onKey = e => { if (e.key === 'Escape') setOpen(false); };
    const onDown = e => { if (!wrapRef.current?.contains(e.target)) setOpen(false); };
    window.addEventListener('keydown', onKey);
    window.addEventListener('mousedown', onDown);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('mousedown', onDown);
    };
  }, [open]);

  const byKey = Object.fromEntries(COLUMNS.map(c => [c.key, c]));
  const { order, hidden } = layout;
  const toggle = k => onChange({ order, hidden: hidden.includes(k) ? hidden.filter(h => h !== k) : [...hidden, k] });
  const arrow = 'w-4 h-4 rounded text-[10px] leading-none text-gray-600 hover:bg-gray-100 disabled:opacity-20 disabled:hover:bg-transparent';

  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen(o => !o)}
        title="Hide, show and reorder columns"
        className="px-2 py-1 bg-white border border-gray-200 text-navy text-[9px] font-semibold rounded hover:bg-gray-50 whitespace-nowrap"
      >⚙ Columns</button>
      {open && (
        <div className="absolute right-0 top-7 z-30 w-56 bg-white border border-gray-200 rounded shadow-lg p-2">
          <div className="text-[8px] font-bold text-gray-500 uppercase px-1">Columns</div>
          <div className="text-[8px] text-gray-400 px-1 mb-1.5">Untick to hide. Drag, or use the arrows, to reorder.</div>
          <ul className="max-h-[60vh] overflow-y-auto">
            {order.map((k, i) => {
              const c = byKey[k];
              return (
                <li key={k} {...dragProps(k, move)} className="flex items-center gap-1.5 px-1 py-0.5 rounded hover:bg-gray-50 cursor-grab">
                  <span className="text-gray-300 text-[10px] select-none" aria-hidden="true">⠿</span>
                  <label className="flex flex-1 min-w-0 items-center gap-1.5 cursor-pointer" title={c.locked ? 'Always shown' : undefined}>
                    <input type="checkbox" className="w-3 h-3 accent-gold" checked={!hidden.includes(k)} disabled={c.locked} onChange={() => toggle(k)} />
                    <span className={`text-[9px] truncate ${hidden.includes(k) ? 'text-gray-400' : 'text-gray-700'}`}>{c.label}</span>
                  </label>
                  <button type="button" aria-label={`Move ${c.label} up`} disabled={i === 0} onClick={() => move(k, order[i - 1])} className={arrow}>↑</button>
                  <button type="button" aria-label={`Move ${c.label} down`} disabled={i === order.length - 1} onClick={() => move(k, order[i + 1])} className={arrow}>↓</button>
                </li>
              );
            })}
          </ul>
          <div className="mt-1.5 pt-1.5 border-t border-gray-100 px-1">
            <button type="button" onClick={() => onChange(resolveLayout(COLUMNS, null))} className="text-[8px] text-blue-500 hover:underline font-semibold">Reset to default</button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function PmoDashboardPage() {
  const [allRows, setRows]    = useState([]);
  const [framework, setFramework] = useState(''); // '' = all SOWs; else one framework's sow_id
  const [loading, setLoading] = useState(true);
  const [search, setSearch]         = useState('');
  const [ragFilter, setRagFilter]   = useState('all');
  const [flagFilter, setFlagFilter] = useState(null);  // overdue | stale | blocked | slipped
  const [colFilters, setColFilters] = useState({});    // column key → filter value
  const [layout, setLayout] = useState(loadLayout);

  useEffect(() => {
    api.get('pmo-dashboard')
      .then(d => setRows(Array.isArray(d) ? d : []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, []);

  const saveLayout = (next) => {
    setLayout(next);
    setColFilters(f => Object.fromEntries(Object.entries(f).filter(([k]) => !next.hidden.includes(k))));
    try { localStorage.setItem(LAYOUT_KEY, JSON.stringify(next)); } catch { /* storage unavailable */ }
  };
  const move = (key, target) => saveLayout({ ...layout, order: moveColumn(layout.order, key, target) });
  const visible = visibleColumns(COLUMNS, layout);

  // The framework view narrows the whole dashboard, counts included, to one
  // framework and its call-offs; the picker still offers every framework.
  const frameworks = frameworkOptions(allRows);
  const rows = scopeToFramework(allRows, framework);

  const setColFilter = (key, v) => setColFilters(f => ({ ...f, [key]: v }));

  const filtered = rows.filter(r => {
    if (r.sow_id === framework) return true; // pinned on top whatever the filters say
    if (ragFilter !== 'all' && r.worst_rag !== ragFilter) return false;
    if (flagFilter === 'overdue' && !isOverdue(r)) return false;
    if (flagFilter === 'stale'   && !r.rag_stale) return false;
    if (flagFilter === 'blocked' && r.delivery_status !== 'blocked') return false;
    if (flagFilter === 'slipped' && !isSlipped(r)) return false;
    if (!passesColumnFilters(r, visible, colFilters)) return false;
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      return (r.reference || '').toLowerCase().includes(q)
          || (r.client_name || '').toLowerCase().includes(q)
          || (r.owner_name  || '').toLowerCase().includes(q)
          || (r.detail      || '').toLowerCase().includes(q);
    }
    return true;
  });

  const anyColFilter = visible.some(c => colFilters[c.key]?.length);
  const clearColFilters = () => setColFilters({});

  const setSiaReference = (sowId, sia) =>
    setRows(rs => rs.map(r => r.sow_id === sowId ? { ...r, sia_reference: sia || null } : r));
  const ctx = { setSiaReference, framework };

  const ragCounts = {
    red:   rows.filter(r => r.worst_rag === 'red').length,
    amber: rows.filter(r => r.worst_rag === 'amber').length,
    green: rows.filter(r => r.worst_rag === 'green').length,
    none:  rows.filter(r => !r.worst_rag).length,
  };

  const flagCounts = {
    overdue: rows.filter(isOverdue).length,
    stale:   rows.filter(r => r.rag_stale).length,
    blocked: rows.filter(r => r.delivery_status === 'blocked').length,
    slipped: rows.filter(isSlipped).length,
  };
  const maxSlip = rows.reduce((mx, r) => Math.max(mx, slipDays(r)), 0);
  const toggleFlag = (k) => setFlagFilter(f => f === k ? null : k);

  if (loading) return <div className="p-6 text-gray-400 text-sm">Loading PMO dashboard…</div>;

  return (
    <div className="fade-in md:h-full md:flex md:flex-col">
      {/* Header */}
      <div className="flex items-center gap-4 mb-4 flex-wrap">
        <div>
          <h2 className="text-base font-extrabold text-navy">PMO Dashboard</h2>
          <div className="text-[9px] text-gray-500 mt-0.5">
            {rows.length} active SOWs · {rows.filter(r => r.next_ms_id).length} with pending milestones
          </div>
        </div>
        <button
          onClick={() => exportToExcel(filtered, visible)}
          disabled={!filtered.length}
          title="Exports the rows and columns the table is showing. Filter the Client column first for one client's report."
          className="disabled:opacity-40 flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-[10px] font-semibold rounded transition-colors"
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
          Export to Excel
        </button>
      </div>

      {/* Health summary tiles — click to filter, click again to clear */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
        {[
          { key: 'overdue', label: 'Overdue milestones', count: flagCounts.overdue, sub: 'past forecast date',          cls: 'text-red-700',    ring: 'ring-red-300',    bg: 'bg-red-50 border-red-200' },
          { key: 'stale',   label: 'Stale updates',       count: flagCounts.stale,   sub: 'older than cadence',          cls: 'text-amber-700',  ring: 'ring-amber-300',  bg: 'bg-amber-50 border-amber-200' },
          { key: 'blocked', label: 'Blocked',             count: flagCounts.blocked, sub: 'delivery status',             cls: 'text-rose-700',   ring: 'ring-rose-300',   bg: 'bg-rose-50 border-rose-200' },
          { key: 'slipped', label: 'At Risk',             count: flagCounts.slipped, sub: maxSlip ? `up to ${maxSlip}d beyond contract` : 'forecast vs contract', cls: 'text-orange-700', ring: 'ring-orange-300', bg: 'bg-orange-50 border-orange-200' },
        ].map(t => (
          <button
            key={t.key}
            onClick={() => toggleFlag(t.key)}
            className={`text-left px-3 py-2 rounded-lg border transition-all ${t.bg} ${flagFilter === t.key ? `ring-2 ${t.ring}` : 'opacity-90 hover:opacity-100'}`}
          >
            <div className={`text-lg font-extrabold leading-none ${t.cls}`}>{t.count}</div>
            <div className="text-[9px] font-bold text-gray-600 mt-0.5">{t.label}</div>
            <div className="text-[8px] text-gray-400">{t.sub}</div>
          </button>
        ))}
      </div>
      {flagFilter && (
        <div className="mb-2 text-[9px] text-gray-500">
          Filtered to <b className="text-gray-700">{flagFilter}</b> · {filtered.length} of {rows.length}
          <button onClick={() => setFlagFilter(null)} className="ml-2 text-blue-500 hover:underline font-semibold">✕ clear</button>
        </div>
      )}

      {/* RAG summary chips */}
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        {[
          { key: 'all',   label: `All (${rows.length})`,        cls: 'bg-gray-100 text-gray-600 border border-gray-200' },
          { key: 'red',   label: `Red (${ragCounts.red})`,      cls: 'bg-red-100 text-red-700 border border-red-200'       },
          { key: 'amber', label: `Amber (${ragCounts.amber})`,  cls: 'bg-amber-100 text-amber-700 border border-amber-200' },
          { key: 'green', label: `Green (${ragCounts.green})`,  cls: 'bg-emerald-100 text-emerald-700 border border-emerald-200' },
          { key: 'none',  label: `No RAG (${ragCounts.none})`,  cls: 'bg-gray-50 text-gray-400 border border-gray-200'    },
        ].map(({ key, label, cls }) => (
          <button
            key={key}
            onClick={() => setRagFilter(key)}
            className={`px-2.5 py-1 text-[9px] font-semibold rounded-full transition-all ${cls} ${ragFilter === key ? 'ring-2 ring-offset-1 ring-navy/30 font-bold' : 'opacity-70 hover:opacity-100'}`}
          >
            {label}
          </button>
        ))}
        {anyColFilter && (
          <button onClick={clearColFilters} className="ml-auto text-[8px] text-blue-500 hover:underline font-semibold whitespace-nowrap">✕ Clear column filters</button>
        )}
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search ref, client, owner…"
          className={`${anyColFilter ? '' : 'ml-auto '}form-input max-w-[200px] text-[9px]`}
        />
        {frameworks.length > 0 && (
          <select
            value={framework}
            onChange={e => setFramework(e.target.value)}
            aria-label="Framework"
            title="Show one framework SOW and its call-offs"
            className={`form-select max-w-[220px] text-[9px] ${framework ? 'border-navy text-navy font-semibold' : ''}`}
          >
            <option value="">All SOWs</option>
            {frameworks.map(([id, label]) => <option key={id} value={id}>Framework: {label}</option>)}
          </select>
        )}
        <ColumnsMenu layout={layout} onChange={saveLayout} move={move} />
      </div>

      {/* Table: on desktop it takes the height left in the window and scrolls
          both ways inside it, so the sideways scroll bar never sits below a
          long list of SOWs. Not a viewport-height cap: the S / M / L zoom scales vh too. */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-auto md:flex-1 md:min-h-[240px] hscroll">
        <table className="w-full text-[10px]">
          <thead className="sticky top-0 z-20">
            <tr className="border-b bg-gray-50">
              {visible.map(c => <th key={c.key} {...dragProps(c.key, move)} title={c.title} className={`px-3 py-2 text-[8px] font-bold uppercase text-gray-500 whitespace-nowrap cursor-grab ${c.align === 'center' ? 'text-center' : 'text-left'}`}>{c.label}</th>)}
            </tr>
            {/* Column filter row */}
            <tr className="border-b border-gray-200 bg-blue-50/40">
              {visible.map(c => <th key={c.key} className="px-2 py-1">{c.filter && <ColumnFilter filter={c.filter} rows={rows} value={colFilters[c.key]} onChange={v => setColFilter(c.key, v)} />}</th>)}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {filtered.length === 0 && (
              <tr>
                <td colSpan={visible.length} className="px-4 py-8 text-center text-gray-400 text-sm">No records match</td>
              </tr>
            )}
            {filtered.map(r => (
              <tr key={r.sow_id} className="hover:bg-gray-50/60 transition-colors">
                {visible.map(c => <td key={c.key} className={`px-3 py-2 align-top ${c.td || ''} ${r.sow_id === framework ? 'bg-amber-50' : ''}`}>{c.cell(r, ctx)}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {filtered.length > 0 && (
        <div className="mt-2 text-[8px] text-gray-400">
          Showing {filtered.length} of {rows.length} active SOWs · RAG = worst of Schedule, Budget, Resource, Risk · 4 dots = S / B / R / R · read-only report apart from SIA Ref (update projects via Project Update)
        </div>
      )}

    </div>
  );
}
